import { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import { Button, Content, Header, SubscriptionModal, Text } from '../common/components';
import { useAppDispatch, useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import { selectSecurity } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import DeadlineFormModal from './components/DeadlineFormModal';
import DeadlineList from './components/DeadlineList';
import DeadlineNotificationSetting from './components/DeadlineNotificationSetting';
import DeadlineSummary from './components/DeadlineSummary';
import DeadlineTip from './components/DeadlineTip';
import {
  completeDeadline,
  completeSystemDeadline,
  createDeadline,
  deleteDeadline,
  getDeadlines,
  getNotificationSetting,
  setNotificationSetting,
  updateDeadline,
} from './services/DeadlineService';
import { Deadline, DeadlineFormValues } from './types';

const namespace = 'Deadlines';

type FilterKey = 'all' | 'pending' | 'completed';
const filters: FilterKey[] = ['all', 'pending', 'completed'];

const matchesFilter = (deadline: Deadline, filter: FilterKey): boolean => {
  switch (filter) {
    case 'pending':
      return deadline.completedAt === null;
    case 'completed':
      return deadline.completedAt !== null;
    default:
      return true;
  }
};

export const Deadlines: React.FC = () => {
  useDocumentTitle(t('title', namespace));
  const dispatch = useAppDispatch();

  // Plný modul jen pro building-subscription (SUBSCRIPTION_FULL) – stejné uzamčení jako Rejstříky.
  const security = useAppSelector(selectSecurity).ui;
  const hasSubscription = (security.tokens?.filter((item) => item.type === 'building-subscription').length || 0) > 0;

  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean | null>(null);

  const [isShown, setIsShown] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'form' | 'delete'>('form');
  const [selected, setSelected] = useState<Deadline | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [items, setting] = await Promise.all([getDeadlines(), getNotificationSetting()]);
        setDeadlines(items);
        setNotificationsEnabled(setting.enabled);
      } catch {
        dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [dispatch]);

  const handleNotificationToggle = async (enabled: boolean) => {
    setNotificationsEnabled(enabled);
    try {
      const setting = await setNotificationSetting(enabled);
      setNotificationsEnabled(setting.enabled);
      dispatch(
        setFlashMessage({
          message: t(setting.enabled ? 'notificationSetting.successOn' : 'notificationSetting.successOff', namespace),
          type: 'success',
        })
      );
    } catch {
      setNotificationsEnabled(!enabled);
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
    }
  };

  const visibleDeadlines = useMemo(
    () => deadlines.filter((deadline) => matchesFilter(deadline, filter)),
    [deadlines, filter]
  );

  const upsertDeadline = (deadline: Deadline) =>
    setDeadlines((prev) => {
      const exists = prev.some((item) => item.id === deadline.id);
      return exists ? prev.map((item) => (item.id === deadline.id ? deadline : item)) : [...prev, deadline];
    });

  const openCreate = () => {
    setSelected(null);
    setModalType('form');
    setIsShown(true);
  };

  const openEdit = (deadline: Deadline) => {
    setSelected(deadline);
    setModalType('form');
    setIsShown(true);
  };

  const openDelete = (deadline: Deadline) => {
    setSelected(deadline);
    setModalType('delete');
    setIsShown(true);
  };

  const handleSubmit = async (values: DeadlineFormValues, id: string | null) => {
    try {
      const saved = id ? await updateDeadline(id, values) : await createDeadline(values);
      upsertDeadline(saved);
      setIsShown(false);
      dispatch(
        setFlashMessage({
          message: t(id ? 'Flash.successEdit' : 'Flash.successAdd', namespace),
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      throw error;
    }
  };

  const completeInFlight = useRef(false);

  const handleComplete = async (deadline: Deadline, completed: boolean) => {
    // guard proti double-clicku – druhý request by na unikátní constraint stejně nic nezměnil
    if (completeInFlight.current) return;
    completeInFlight.current = true;

    try {
      if (deadline.source === 'system') {
        if (!deadline.deadlineKey) return;
        setDeadlines(await completeSystemDeadline(deadline.deadlineKey, deadline.dueDate, completed));
      } else {
        upsertDeadline(await completeDeadline(deadline.id, completed));
      }
      dispatch(
        setFlashMessage({
          message: t(completed ? 'Flash.successComplete' : 'Flash.successUndo', namespace),
          type: 'success',
        })
      );
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
    } finally {
      completeInFlight.current = false;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDeadline(id);
      setDeadlines((prev) => prev.filter((item) => item.id !== id));
      setIsShown(false);
      dispatch(setFlashMessage({ message: t('Flash.successDelete', namespace), type: 'success' }));
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
    }
  };

  return (
    <>
      <Header
        title={t('heading', namespace)}
        backlink={-1}
        actions={hasSubscription && <Button size="small" title={t('addAction', namespace)} onClick={openCreate} />}
      />

      <Content className="Content Deadlines" isLoading={isLoading}>
        <Text as="div" size="body-medium" color="Neutral600" className="mb-3">
          {t('subtitle', namespace)}
        </Text>

        {hasSubscription && deadlines.filter((deadline) => deadline.source === 'custom').length < 3 && (
          <DeadlineTip onAdd={openCreate} />
        )}

        <DeadlineSummary deadlines={deadlines} />

        <div className="DeadlineLock">
          <div className={cx(!hasSubscription && 'DeadlineLock__blur')}>
            <div className="DeadlineFilter">
              {filters.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={cx('DeadlineFilter__item', filter === key && 'DeadlineFilter__item--active')}
                  onClick={() => setFilter(key)}
                >
                  {t(`filter.${key}`, namespace)}
                </button>
              ))}
            </div>

            <DeadlineList
              deadlines={visibleDeadlines}
              onComplete={handleComplete}
              onEdit={openEdit}
              onDelete={openDelete}
            />

            {hasSubscription && (
              <DeadlineNotificationSetting enabled={notificationsEnabled} onChange={handleNotificationToggle} />
            )}
          </div>

          {!hasSubscription && (
            <SubscriptionModal title={t('lock.title', namespace)} text={t('lock.text', namespace)} />
          )}
        </div>
      </Content>

      <DeadlineFormModal
        isShown={isShown}
        modalType={modalType}
        selected={selected}
        onHide={() => setIsShown(false)}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />
    </>
  );
};

export default Deadlines;
